import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ProfileResponseDto,
  ProfileService,
  UserRoleDto,
} from 'src/generated-sources/openapi/configuration-service/profile';
import { ProfileAccessDenialCode, ProfileLoadResult } from './profile-access.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileDataService {
  private readonly userProfile$ = new BehaviorSubject<ProfileResponseDto | null>(null);
  private readonly isFetchingProfile$ = new BehaviorSubject<boolean>(false);

  constructor(private readonly profileService: ProfileService) {}

  public get currentProfile(): ProfileResponseDto | null {
    return this.userProfile$.getValue();
  }

  public loadProfile(): Observable<ProfileLoadResult> {
    if (this.isFetchingProfile$.value && this.currentProfile) {
      return of({ status: 'success', profile: this.currentProfile });
    }

    this.isFetchingProfile$.next(true);

    return this.profileService.loadProfile().pipe(
      tap((profile) => {
        this.userProfile$.next(profile);
        this.isFetchingProfile$.next(false);
      }),
      map((profile): ProfileLoadResult => ({ status: 'success', profile })),
      catchError((error: unknown) => {
        this.userProfile$.next(null);
        this.isFetchingProfile$.next(false);
        return of(this.mapErrorToResult(error));
      }),
    );
  }

  /**
   * Normalizes the HTTP failure into a `ProfileLoadResult`.
   *
   * In denial scenarios, a payload in the following format is expected:
   *   { "message": "...", "errors": [{ "code": "DENIED_NO_PRODUCT" | "DENIED_INACTIVE", "message": "..." }] }
   */
  private mapErrorToResult(error: unknown): ProfileLoadResult {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      const code = (error.error?.errors?.[0]?.code ?? '') as string;
      if (code === ProfileAccessDenialCode.NoProduct) {
        return { status: 'denied', code: ProfileAccessDenialCode.NoProduct };
      }
      if (code === ProfileAccessDenialCode.InactiveAccount) {
        return { status: 'denied', code: ProfileAccessDenialCode.InactiveAccount };
      }
    }
    return { status: 'error' };
  }

  public clearProfile(): void {
    this.userProfile$.next(null);
    this.isFetchingProfile$.next(false);
  }

  public getUserProfile$() {
    return this.userProfile$.asObservable();
  }

  public isFetchingUserProfile$() {
    return this.isFetchingProfile$.asObservable();
  }

  public isUserAdmin(): boolean {
    const currentProfile = this.currentProfile;
    return currentProfile?.userRoles?.includes(UserRoleDto.Admin) ?? false;
  }
}
